import React, { FunctionComponent, useContext, useState } from "react";
import { connect } from "react-redux";
import { AppBar, Box, Divider, Fade, IconButton, Toolbar, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import {
    AccountTree,
    CameraRoll,
    Description,
    History,
    Image,
    Menu as MenuIcon,
    MenuBook,
    Translate,
    VideogameAsset,
    ViewComfy
}
    from "@mui/icons-material";
import { selectNetwork, selectProjects, selectUser } from "../../redux/selectors/firebase";
import { PathwayUser, ProjectRoles, Projects } from "../../redux/types/firebase";
import { selectDirty, selectMode, selectProject, selectTitle, selectHelpAvailable } from "../../redux/selectors/editor";
import { updateFocus, updateMode, updateModeData, UpdateModeData, updateProject } from "../../redux/slices/editor";
import NavigationMenu from "./NavigationMenu";
import GraphTools, { IGraphToolsProps } from "./tools/GraphTools";
import ScriptTools from "./tools/ScriptTools";
import GameTools, { GameToolsProps } from "./tools/GameTools";
import WriterTools from "./tools/WriterTools";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import NotificationsList from "../notifications/Notifications"
import UserMenu from "./UserMenu";
import GraphMenu from "./menus/GraphMenu";
import HelpMenu from "./menus/HelpMenu";
import { clearURLSearchParam } from "../../utility/url";
import { NetworkData } from "../../fstn/network";
import {LocalizationTools} from "./tools/LocalizationTools";

const useStyles = makeStyles(theme => ({
    bar: {
        backgroundColor: "transparent",
        boxShadow: "none",
        position: "absolute",
        top: 0,
        height: 64,
        left: 0,
        right: 0,
        zIndex: 1000,
    },
    icon: {
        borderRadius: 24,
        color: "white",
        margin: 4
    },
    iconActive: {
        backgroundColor: theme.palette.primary.dark,
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    root: {
        flexGrow: 1,
    },
    title: {
        flexGrow: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },
}));

interface Props
{
    gameTools: GameToolsProps
    graphTools: IGraphToolsProps
    mode: string
    network?: NetworkData
    onResetNetwork: () => any;
    onSetMode: (event: React.MouseEvent<HTMLElement>) => void
    onShowDialog: (dialog: string) => void
    project: string
    projects: Projects
    title: string
    updateFocus: (project: string) => void
    updateModeData: UpdateModeData
    updateMode: (mode: string) => void
    updateProject: (project: string) => void
    user: PathwayUser
}
function NavBar({ gameTools, graphTools, mode, network, onResetNetwork, onSetMode, onShowDialog, project,
    projects, updateFocus, updateMode, updateModeData, updateProject, title, user }: Props)
{
    const classes = useStyles();
    const [navMenuAnchor, setNavMenuAnchor] = useState<Element>();
    const [faded, setFaded] = useState(false);
    const { untrackNetwork } = useContext(FirebaseContext);

    if (!projects) return null;

    const handleMenuClick = ({ currentTarget }: React.MouseEvent) => setNavMenuAnchor(currentTarget);
    const handleCloseNavMenu = () => setNavMenuAnchor(undefined);
    const handleCloseProject = () =>
    {
        clearURLSearchParam("state");
        clearURLSearchParam("project");
        localStorage.setItem("save", "{}");
        untrackNetwork(project);
        updateMode("");
        updateFocus("");
        updateProject("")
        updateModeData({
            selected: [],
            selectedActive: ""
        });
    };
    const handleMouseEnter = () => setFaded(false);
    const handleMouseLeave = () => setFaded(mode === "game");
    const admin = user.roles.admin;
    const setTop = Boolean(projects[project]?.setTop);
    const editor = admin || user.roles.editor || (user.roles as ProjectRoles)[project]?.editor;
    const reviewer = editor || user.roles.reviewer || (user.roles as ProjectRoles)[project]?.reviewer;
    const editDisabled = network === undefined || !editor;
    const tools = new Map<string, any>(Object.entries({
        graph: <GraphTools {...graphTools} />,
        game: <GameTools {...gameTools} />,
        localization: <LocalizationTools/>,
        script: <ScriptTools />,
        writer: <WriterTools project={project} />
    }));
    const modeMenus: { [mode: string]: FunctionComponent<any> } = {
        graph: GraphMenu
    }
    const modeNames: { [mode: string]: string } = {
        graph: "Flow Mode",
        writer: "scriptwriter Mode",
        script: "Script View Mode",
        intertitle: "Text Playable Mode",
        localization: "Localization",
        sheets: "Spreadsheet Mode"
    }
    const titleOverride = ["graph"].includes(mode) || mode === undefined ? title : mode.toUpperCase();
    const modeName = modeNames[mode] || mode;
    const iconClass = (icon: string) => mode === icon ? [classes.icon, classes.iconActive].join(" ") : classes.icon;

    return <>
        <Box onMouseLeave={handleMouseLeave}>
            <Fade in={!faded} timeout={250}>
                <AppBar position="fixed">
                    <Toolbar>
                        <IconButton edge="start" color="inherit" className={classes.menuButton}
                            onClick={handleMenuClick} size="large">
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" className={classes.title} title={titleOverride}>
                            {titleOverride}
                        </Typography>
                        {mode && tools.get(mode)}
                        <Divider orientation="vertical" flexItem />
                        <IconButton className={iconClass("localization")} disabled={(editDisabled)}
                            data-mode="localization" title="Localization" onClick={onSetMode}>
                            <Translate/>
                        </IconButton>
                        <IconButton className={iconClass("writer")} disabled={(editDisabled || !reviewer)}
                            data-mode="writer" title="Scriptwriter view mode" onClick={onSetMode}>
                            <Description />
                        </IconButton>
                        <IconButton className={iconClass("graph")} data-mode="graph" disabled={editDisabled}
                            title="Graph view mode" onClick={onSetMode}>
                            <AccountTree />
                        </IconButton>
                        <IconButton className={iconClass("game")} data-mode="game"
                            disabled={network === undefined || !setTop}
                            title="Play through mode" onClick={onSetMode}>
                            <VideogameAsset />
                        </IconButton>
                        <IconButton className={iconClass("script")} data-mode="script"
                            disabled={network === undefined}
                            title="Script view mode" onClick={onSetMode}>
                            <MenuBook />
                        </IconButton>
                        <IconButton className={iconClass("intertitle")} data-mode="intertitle"
                            disabled={network === undefined}
                            title="Intertitle view mode" onClick={onSetMode}>
                            <Image />
                        </IconButton>
                        <IconButton className={iconClass("sheet")}
                            disabled={editDisabled} color="inherit" data-mode="sheet"
                            title="Sheet view mode" onClick={onSetMode} size="large">
                            <ViewComfy />
                        </IconButton>
                        <IconButton className={iconClass("revisions")}
                            disabled={editDisabled} color="inherit" data-mode="revisions"
                            title="Show node revisions" onClick={onSetMode} size="large">
                            <History/>
                        </IconButton>
                        <IconButton disabled={editDisabled || mode === "assets"} color="inherit" data-mode="assets"
                            title="Asset management dashboard" onClick={onSetMode} size="large">
                            <CameraRoll />
                        </IconButton>
                        <HelpMenu onShowDialog={onShowDialog} title={`View ${modeName} Help`} />
                        <Divider orientation="vertical" flexItem />
                        <NotificationsList />
                        <UserMenu onCloseProject={handleCloseProject} onShowDialog={onShowDialog} />
                    </Toolbar>
                </AppBar>
            </Fade>
            <AppBar className={classes.bar} onMouseEnter={handleMouseEnter} onMouseMove={handleMouseEnter} />
        </Box>
        <NavigationMenu
            anchor={navMenuAnchor} project={project} modeMenu={modeMenus[mode]} name={modeName}
            onResetNetwork={onResetNetwork} onCloseProject={handleCloseProject} network={network}
            onClose={handleCloseNavMenu} onShowDialog={onShowDialog} />
    </>;
}

const mapStateToProps = (store: never) => ({
    dirty: selectDirty(store),
    mode: selectMode(store),
    helpAvailable: selectHelpAvailable(store),
    network: selectNetwork(store),
    project: selectProject(store),
    projects: selectProjects(store),
    title: selectTitle(store),
    user: selectUser(store),
});

export default connect(mapStateToProps, {
    updateFocus,
    updateMode,
    updateModeData,
    updateProject
})(NavBar);
